import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { connectAdminSocket } from '../socket';
import { sendBrowserNotification } from '../utils/browserNotification';

const formatWon = (amount) => `₩${Number(amount || 0).toLocaleString('ko-KR')}`;

const formatDate = () => {
  const now = new Date();
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return `${now.getFullYear()}년 ${now.getMonth() + 1}월 ${now.getDate()}일 (${days[now.getDay()]}요일)`;
};

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const diff = Math.floor((new Date() - new Date(dateStr)) / 60000);
  if (diff < 1) return '방금 전';
  if (diff < 60) return `${diff}분 전`;
  const h = Math.floor(diff / 60);
  return `${h}시간 전`;
};

// Simple toast hook
let toastIdCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastIdCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);

  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconX = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconWarning = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const IconBell = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">
          {t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconX /> : t.type === 'warning' ? <IconWarning /> : <IconBell />}
        </span>
        <div className="toast-content">
          <div className="toast-message">{t.msg}</div>
        </div>
      </div>
    ))}
  </div>
);

// Enhanced dashboard extras component
const DashboardExtras = ({ storeId, navigate }) => {
  const [hourlyData, setHourlyData] = useState([]);
  const [topMenus, setTopMenus] = useState([]);
  const [staffCallCount, setStaffCallCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchExtras = async () => {
      const today = new Date().toISOString().slice(0, 10);
      try {
        const [dailyRes, summaryRes, callsRes] = await Promise.allSettled([
          storeId ? api.get(`/admin/reports/${storeId}/daily?date=${today}`) : Promise.reject(),
          storeId ? api.get(`/admin/reports/${storeId}/summary`) : Promise.reject(),
          storeId ? api.get(`/admin/staff-calls/${storeId}`) : Promise.reject(),
        ]);

        if (dailyRes.status === 'fulfilled') {
          const d = dailyRes.value.data;
          setHourlyData(Array.isArray(d) ? d : d.hourly || d.data || []);
        } else {
          setHourlyData([]);
        }

        if (summaryRes.status === 'fulfilled') {
          const s = summaryRes.value.data;
          setTopMenus(s.topMenus || s.top_menus || []);
        } else {
          setTopMenus([]);
        }

        if (callsRes.status === 'fulfilled') {
          const d = callsRes.value.data;
          const list = Array.isArray(d) ? d : d.calls || d.staffCalls || [];
          setStaffCallCount(list.filter((c) => c.status === 'pending').length);
        } else {
          setStaffCallCount(0);
        }
      } catch {
        setHourlyData([]);
        setTopMenus([]);
        setStaffCallCount(0);
      } finally {
        setLoaded(true);
      }
    };
    fetchExtras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  if (!loaded) return null;

  const maxRevenue = Math.max(...hourlyData.map((d) => d.revenue || 0), 1);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, marginBottom: 24 }}>
      {/* Today's hourly revenue chart */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">오늘의 매출</span>
          <button className="btn btn-sm btn-outline" onClick={() => navigate('/reports')}>
            상세 보기 &rarr;
          </button>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 140 }}>
            {hourlyData.map((d, i) => {
              const pct = Math.max((d.revenue / maxRevenue) * 100, d.revenue > 0 ? 2 : 0);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                  <div
                    className="chart-bar"
                    title={`${d.hour}시: ${formatWon(d.revenue)}`}
                    style={{
                      width: '100%', maxWidth: 20, height: `${pct}%`,
                      background: 'var(--primary)', borderRadius: '3px 3px 0 0',
                      minHeight: d.revenue > 0 ? 3 : 0, opacity: 0.8, cursor: 'pointer',
                      animationDelay: `${i * 30}ms`,
                    }}
                  />
                  {i % 4 === 0 && (
                    <div style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 4 }}>{d.hour}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right column: Top menus + Staff calls */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Popular menus */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">인기 메뉴 TOP 5</span>
          </div>
          <div className="card-body" style={{ padding: '12px 20px' }}>
            {topMenus.slice(0, 5).map((menu, idx) => (
              <div key={idx} className="animate-slide-in-right" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: idx < 4 ? '1px solid var(--border)' : 'none', animationDelay: `${idx * 50}ms` }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '0.7rem', flexShrink: 0,
                  background: idx === 0 ? 'var(--primary)' : idx === 1 ? 'var(--primary)' : idx === 2 ? 'var(--warning)' : 'var(--border)',
                  color: idx < 3 ? '#fff' : 'var(--text-light)',
                }}>
                  {idx + 1}
                </span>
                <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: 500 }}>{menu.name}</span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{menu.count}건</span>
              </div>
            ))}
          </div>
        </div>

        {/* Staff call count */}
        <div
          className="card"
          style={{ cursor: 'pointer', transition: 'var(--transition)' }}
          onClick={() => navigate('/staff-calls')}
        >
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px' }}>
            <div style={{
              width: 44, height: 44, borderRadius: 'var(--radius-sm)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: staffCallCount > 0 ? 'var(--danger-light)' : 'var(--border)',
              fontSize: '1.2rem',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={staffCallCount > 0 ? 'var(--danger)' : 'var(--text-muted)'} strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>직원 호출</div>
              <div style={{ fontSize: '0.78rem', color: staffCallCount > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                {staffCallCount > 0 ? `대기 ${staffCallCount}건` : '대기 호출 없음'}
              </div>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toasts, addToast } = useToast();

  const [tables, setTables] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [stats, setStats] = useState({
    todayRevenue: 0,
    activeTableCount: 0,
    pendingOrderCount: 0,
    completedOrderCount: 0,
  });
  const [operationMode, setOperationMode] = useState('post');
  const [isLoadingMode, setIsLoadingMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const socketRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  const storeId = user?.storeId || user?.store_id;
  const storeName = user?.name || user?.storeName || user?.store_name || '내 가게';

  const playNotificationBeep = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } catch (e) {
      // Ignore audio errors
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!storeId) {
      setTables([]);
      setRecentOrders([]);
      setStats({ todayRevenue: 0, activeTableCount: 0, pendingOrderCount: 0, completedOrderCount: 0 });
      setIsLoading(false);
      return;
    }

    try {
      const [tablesRes, ordersRes, statsRes, settingsRes, dashRes] = await Promise.allSettled([
        api.get(`/stores/${storeId}/tables`),
        api.get(`/stores/${storeId}/orders?limit=10&sort=-createdAt`),
        api.get(`/stores/${storeId}/stats/today`),
        api.get(`/admin/stores/${storeId}`),
        api.get(`/admin/dashboard/${storeId}`),
      ]);

      if (tablesRes.status === 'fulfilled') {
        const data = tablesRes.value.data;
        setTables(Array.isArray(data) ? data : data.tables || []);
      }

      if (ordersRes.status === 'fulfilled') {
        const data = ordersRes.value.data;
        setRecentOrders(Array.isArray(data) ? data : data.orders || []);
      }

      // Use dashboard endpoint stats first (most complete), fall back to stats/today
      if (dashRes.status === 'fulfilled') {
        const d = dashRes.value.data || dashRes.value;
        const s = d.stats || {};
        setStats({
          todayRevenue: s.todayRevenue ?? s.today_revenue ?? 0,
          activeTableCount: s.activeTables ?? s.activeTableCount ?? s.active_tables ?? 0,
          pendingOrderCount: s.pendingOrders ?? s.pendingOrderCount ?? s.pending_orders ?? 0,
          completedOrderCount: s.completedOrders ?? s.completedOrderCount ?? s.completed_orders ?? 0,
        });
        if (d.recentOrders) setRecentOrders(d.recentOrders);
      } else if (statsRes.status === 'fulfilled') {
        const s = statsRes.value.data || statsRes.value;
        setStats({
          todayRevenue: s.todayRevenue ?? s.today_revenue ?? 0,
          activeTableCount: s.activeTableCount ?? s.activeTables ?? s.active_tables ?? 0,
          pendingOrderCount: s.pendingOrderCount ?? s.pendingOrders ?? s.pending_orders ?? 0,
          completedOrderCount: s.completedOrderCount ?? s.completedOrders ?? s.completed_orders ?? 0,
        });
      }

      if (settingsRes.status === 'fulfilled') {
        const store = settingsRes.value.data?.store || settingsRes.value.data;
        if (store?.operationMode) setOperationMode(store.operationMode);
      }

      // Compute stats from tables/orders if stats endpoint failed
      if (statsRes.status === 'rejected') {
        const tableData = tablesRes.status === 'fulfilled'
          ? (Array.isArray(tablesRes.value.data) ? tablesRes.value.data : tablesRes.value.data.tables || [])
          : [];
        const orderData = ordersRes.status === 'fulfilled'
          ? (Array.isArray(ordersRes.value.data) ? ordersRes.value.data : ordersRes.value.data.orders || [])
          : [];

        setStats({
          todayRevenue: orderData.filter((o) => o.status === 'served').reduce((s, o) => s + (o.totalAmount || 0), 0),
          activeTableCount: tableData.filter((t) => t.status === 'occupied' || t.status === 'payment_pending').length,
          pendingOrderCount: orderData.filter((o) => o.status === 'pending').length,
          completedOrderCount: orderData.filter((o) => o.status === 'served').length,
        });
      }
    } catch {
      setTables([]);
      setRecentOrders([]);
      setStats({ todayRevenue: 0, activeTableCount: 0, pendingOrderCount: 0, completedOrderCount: 0 });
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchData();

    // Socket.io
    if (storeId) {
      const socket = connectAdminSocket(storeId);
      socketRef.current = socket;

      socket.on('new_order', (data) => {
        const tableNum = data.tableNumber || data.table_number;
        playNotificationBeep();
        addToast(`새 주문! 테이블 ${tableNum}번`, 'info');
        sendBrowserNotification('새 주문 접수', `테이블 ${tableNum}번 주문이 들어왔습니다`);
        setRecentOrders((prev) => [data, ...prev.slice(0, 9)]);
        setStats((s) => ({ ...s, pendingOrderCount: s.pendingOrderCount + 1 }));
      });

      socket.on('table_status_change', (data) => {
        setTables((prev) =>
          prev.map((t) => (t._id === data.tableId || t.id === data.tableId ? { ...t, ...data } : t))
        );
      });

      socket.on('order_status_change', (data) => {
        setRecentOrders((prev) =>
          prev.map((o) => (o._id === data.orderId || o.id === data.orderId ? { ...o, status: data.status } : o))
        );
      });

      socket.on('staff_call', (data) => {
        const tableNum = data.tableNumber || data.table_number;
        playNotificationBeep();
        addToast(`직원 호출! 테이블 ${tableNum}번`, 'warning');
        sendBrowserNotification('직원 호출', `테이블 ${tableNum}번에서 호출합니다`);
      });
    }

    // Auto-refresh every 30s
    refreshIntervalRef.current = setInterval(fetchData, 30000);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('new_order');
        socketRef.current.off('table_status_change');
        socketRef.current.off('order_status_change');
        socketRef.current.off('staff_call');
      }
      clearInterval(refreshIntervalRef.current);
    };
  }, [fetchData, storeId, addToast, playNotificationBeep]);

  const handleToggleMode = async () => {
    const newMode = operationMode === 'pre' ? 'post' : 'pre';
    setIsLoadingMode(true);
    try {
      await api.patch(`/admin/stores/${storeId}`, { operationMode: newMode });
      setOperationMode(newMode);
      addToast(`운영 모드가 ${newMode === 'pre' ? '선결제' : '후결제'}로 변경되었습니다.`, 'success');
    } catch {
      addToast('운영 모드 변경에 실패했습니다.', 'error');
    } finally {
      setIsLoadingMode(false);
    }
  };

  const getTableStatusLabel = (status) => {
    const map = { empty: '빈 테이블', occupied: '사용 중', payment_pending: '결제 중', payment_done: '결제 완료' };
    return map[status] || status;
  };

  const getTableStatusDot = (status) => {
    const map = { empty: 'gray', occupied: 'green', payment_pending: 'yellow', payment_done: 'blue' };
    return map[status] || 'gray';
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 28 }}>
          <div className="skeleton-line h-24 w-60" />
          <div className="skeleton-line w-40" style={{ height: 12, marginTop: 8 }} />
        </div>
        <div className="stats-grid">
          {[0,1,2,3].map((i) => (
            <div key={i} className="skeleton-card animate-fade-in-up" style={{ animationDelay: `${i * 60}ms`, display: 'flex', alignItems: 'center', gap: 16 }}>
              <div className="skeleton-circle" />
              <div style={{ flex: 1 }}>
                <div className="skeleton-line w-40" style={{ height: 10 }} />
                <div className="skeleton-line w-60 h-24" />
                <div className="skeleton-line w-40" style={{ height: 10 }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, marginTop: 24 }}>
          <div className="skeleton-card" style={{ height: 300 }}>
            <div className="skeleton-line w-40 h-24" />
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 200, marginTop: 20 }}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} style={{ flex: 1, background: 'linear-gradient(90deg, #F2F4F6 25%, #E8EAED 50%, #F2F4F6 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s ease infinite', borderRadius: '4px 4px 0 0', height: `${20 + Math.random() * 60}%` }} />
              ))}
            </div>
          </div>
          <div className="skeleton-card" style={{ height: 300 }}>
            <div className="skeleton-line w-60 h-24" />
            {[0,1,2,3,4].map((i) => (
              <div key={i} className="skeleton-line w-80" style={{ marginTop: 16 }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const activeTables = tables.filter((t) => t.status === 'occupied' || t.status === 'payment_pending');

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="page-title">
            안녕하세요, {storeName} 사장님!
          </h1>
          <p className="page-subtitle">{formatDate()}</p>
        </div>

        {/* Operation mode toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'white', padding: '12px 16px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-card)' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: 2 }}>운영 모드</div>
            <div style={{ fontSize: '0.875rem', fontWeight: 700, color: operationMode === 'pre' ? 'var(--primary)' : 'var(--success)' }}>
              {operationMode === 'pre' ? '선결제' : '후결제'}
            </div>
          </div>
          <label className="toggle-switch" style={{ cursor: isLoadingMode ? 'not-allowed' : 'pointer' }}>
            <div
              className={`toggle-track ${operationMode === 'pre' ? 'on' : ''}`}
              onClick={!isLoadingMode ? handleToggleMode : undefined}
              style={{ cursor: isLoadingMode ? 'not-allowed' : 'pointer' }}
            >
              <div className="toggle-thumb" />
            </div>
          </label>
        </div>
      </div>

      {/* Stats row */}
      <div className="stats-grid">
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '0ms' }}>
          <div className="stat-icon purple">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">오늘 매출</div>
            <div className="stat-value stat-number">{formatWon(stats.todayRevenue)}</div>
            <div className="stat-change">오늘 기준</div>
          </div>
        </div>
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '60ms' }}>
          <div className="stat-icon green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9h18M3 15h18M9 3v18M15 3v18" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">활성 테이블</div>
            <div className="stat-value stat-number">{stats.activeTableCount}</div>
            <div className="stat-change">전체 {tables.length}개 중</div>
          </div>
        </div>
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '120ms' }}>
          <div className="stat-icon orange">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <circle cx="3" cy="6" r="1" fill="currentColor"/>
              <circle cx="3" cy="12" r="1" fill="currentColor"/>
              <circle cx="3" cy="18" r="1" fill="currentColor"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">대기 주문</div>
            <div className="stat-value stat-number" style={{ color: stats.pendingOrderCount > 0 ? 'var(--danger)' : 'var(--text)' }}>
              {stats.pendingOrderCount}
            </div>
            <div className="stat-change" style={{ color: stats.pendingOrderCount > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
              {stats.pendingOrderCount > 0 ? '빠른 처리가 필요합니다' : '모두 처리됨'}
            </div>
          </div>
        </div>
        <div className="stat-card animate-fade-in-up" style={{ animationDelay: '180ms' }}>
          <div className="stat-icon blue">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div className="stat-info">
            <div className="stat-label">완료 주문</div>
            <div className="stat-value stat-number">{stats.completedOrderCount}</div>
            <div className="stat-change">오늘 기준</div>
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }}>
        {/* Table status mini-grid */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">테이블 현황</span>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => navigate('/tables')}
            >
              전체 보기 →
            </button>
          </div>
          <div className="card-body">
            {tables.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 9h18M3 15h18M9 3v18M15 3v18" strokeLinecap="round"/>
                  </svg>
                </div>
                <h3>테이블이 없습니다</h3>
                <p>테이블 관리에서 테이블을 추가해주세요.</p>
                <button className="btn btn-primary mt-16" onClick={() => navigate('/tables')}>
                  테이블 추가
                </button>
              </div>
            ) : (
              <div className="table-grid">
                {tables.map((table) => (
                  <div
                    key={table._id || table.id}
                    className={`table-card ${table.status || 'empty'}`}
                    onClick={() => navigate('/tables')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="table-card-num">
                      {table.tableNumber || table.table_number}번
                    </div>
                    <div className="table-card-status">
                      <span className={`pulse-dot ${getTableStatusDot(table.status || 'empty')}`} />
                      <span style={{ fontSize: '0.75rem' }}>{getTableStatusLabel(table.status || 'empty')}</span>
                    </div>
                    {(table.status === 'occupied' || table.status === 'payment_pending') && table.currentAmount != null && (
                      <div className="table-card-amount" style={{ marginTop: 4 }}>
                        {formatWon(table.currentAmount || table.current_amount)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Status legend */}
            {tables.length > 0 && (
              <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
                {[
                  { status: 'empty', label: '비어있음', dotClass: 'gray' },
                  { status: 'occupied', label: '사용 중', dotClass: 'green' },
                  { status: 'payment_pending', label: '결제 중', dotClass: 'yellow' },
                  { status: 'payment_done', label: '결제 완료', dotClass: 'blue' },
                ].map(({ status, label, dotClass }) => {
                  const count = tables.filter((t) => (t.status || 'empty') === status).length;
                  return (
                    <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      <span className={`pulse-dot ${dotClass}`} style={{ animation: 'none' }} />
                      {label} ({count})
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent orders feed */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">최근 주문</span>
            <button className="btn btn-sm btn-outline" onClick={() => navigate('/orders')}>
              전체 보기
            </button>
          </div>
          <div className="card-body">
            {recentOrders.length === 0 ? (
              <div className="empty-state" style={{ padding: '30px 0' }}>
                <div className="empty-state-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <circle cx="3" cy="6" r="1" fill="currentColor"/>
                    <circle cx="3" cy="12" r="1" fill="currentColor"/>
                    <circle cx="3" cy="18" r="1" fill="currentColor"/>
                  </svg>
                </div>
                <p>최근 주문이 없습니다</p>
              </div>
            ) : (
              <div className="orders-feed">
                {recentOrders.slice(0, 10).map((order) => {
                  const items = order.items || order.orderItems || [];
                  const itemSummary = items.length > 0
                    ? `${items[0].name || items[0].menuName || '메뉴'}${items.length > 1 ? ` 외 ${items.length - 1}종` : ''}`
                    : '주문';
                  const statusLabel = { pending: '대기중', accepted: '조리중', served: '서빙완료', cancelled: '취소' }[order.status] || order.status;
                  const statusColor = { pending: 'var(--danger)', accepted: 'var(--primary)', served: 'var(--success)', cancelled: 'var(--text-tertiary)' }[order.status] || 'var(--text-tertiary)';

                  return (
                    <div key={order._id || order.id} className="order-feed-item">
                      <div className="order-feed-table">
                        {order.tableNumber || order.table_number}
                      </div>
                      <div className="order-feed-info">
                        <div className="order-feed-desc">
                          테이블 {order.tableNumber || order.table_number}번 · {itemSummary}
                        </div>
                        <div className="order-feed-time">
                          <span style={{ color: statusColor, fontWeight: 600 }}>{statusLabel}</span>
                          {' · '}
                          {timeAgo(order.createdAt || order.created_at)}
                        </div>
                      </div>
                      <div className="order-feed-amount">
                        {formatWon(order.totalAmount || order.total_amount)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced: Today's revenue chart + popular menus + staff calls */}
      <DashboardExtras storeId={storeId} navigate={navigate} />

      {/* Active tables summary */}
      {activeTables.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-header">
            <span className="card-title">활성 테이블 상세</span>
            <span className="badge badge-occupied">{activeTables.length}개 활성</span>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {activeTables.map((table) => (
                <div
                  key={table._id || table.id}
                  style={{
                    padding: '14px 16px',
                    background: 'var(--bg)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>
                      {table.tableNumber || table.table_number}번 테이블
                    </span>
                    <span className={`badge badge-${table.status}`}>
                      {getTableStatusLabel(table.status)}
                    </span>
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--primary)' }}>
                    {formatWon(table.currentAmount || table.current_amount || 0)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
